import { api, ProjectDetail, TaskItem, Epic, ProjectAccess } from '../api';
import { navigate, refresh } from '../lib/router';
import { escapeHtml } from '../lib/utils';
import { renderOverview } from './overview';
import { renderTasks } from './tasks';
import { renderSprints } from './sprints';
import { renderEpics } from './epics';
import { renderEpicDetail } from './epicDetail';
import { renderMembers } from './members';
import { renderReports } from './reports';
import { openEditProjectModal } from './projectEdit';
import { openShareModal } from '../components/shareModal';

export interface ProjectContext {
  project: ProjectDetail;
  tasks: TaskItem[];
  epics: Epic[];
  readOnly: boolean;
  canComment: boolean;
  goTab: (tab: string) => void;
  goEpics: () => void;
  goEpic: (epicId: string) => void;
  projectHref: (tab: string, epicId?: string) => string;
  reload: () => void;
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'epics', label: 'Epics' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'sprints', label: 'Sprints' },
  { key: 'members', label: 'Members' },
  { key: 'reports', label: 'Reports' },
];

interface ProjectRenderOptions {
  id: string;
  tab?: string;
  subId?: string;
  shareToken?: string;
  publicView?: boolean;
}

export async function renderProject(
  app: HTMLElement,
  id: string,
  tab: string,
  subId?: string
): Promise<void> {
  await renderProjectInternal(app, { id, tab, subId });
}

export async function renderPublicProject(
  app: HTMLElement,
  id: string,
  tab = 'overview',
  subId?: string
): Promise<void> {
  await renderProjectInternal(app, { id, tab, subId, publicView: true });
}

export async function renderSharedProject(
  app: HTMLElement,
  token: string,
  tab = 'overview',
  subId?: string
): Promise<void> {
  app.innerHTML = `
    <div class="project-shell"><div class="spinner">Opening project…</div></div>
  `;

  let project: ProjectDetail;
  try {
    const res = await api.get<{ project: ProjectDetail }>(`/share/${token}`);
    project = res.project;
  } catch (e: any) {
    app.innerHTML = `
      <div class="project-shell">
        <div class="error-state">
          <h3>Share link unavailable</h3>
          <p class="muted">${escapeHtml(e.message)}</p>
          <a class="btn btn-primary" href="#/">Go home</a>
        </div>
      </div>
    `;
    return;
  }

  await renderProjectInternal(app, {
    id: project.id,
    tab,
    subId,
    shareToken: token,
  });
}

async function renderProjectInternal(
  app: HTMLElement,
  opts: ProjectRenderOptions
): Promise<void> {
  const { id, tab = 'overview', subId, shareToken, publicView } = opts;

  app.innerHTML = `
    <div class="project-shell"><div class="spinner">Loading project…</div></div>
  `;

  let project: ProjectDetail;
  let tasks: TaskItem[];
  let epics: Epic[];
  try {
    const [p, t, e] = await Promise.all([
      api.get<{ project: ProjectDetail }>(`/projects/${id}`),
      api.get<{ tasks: TaskItem[] }>(`/projects/${id}/tasks`),
      api.get<{ epics: Epic[] }>(`/projects/${id}/epics`),
    ]);
    project = p.project;
    tasks = t.tasks;
    epics = e.epics;
  } catch (e: any) {
    app.innerHTML = `
      <div class="project-shell">
        <div class="error-state">
          <h3>Project not available</h3>
          <p class="muted">${escapeHtml(e.message)}</p>
          <button class="btn btn-primary" data-back>${
            shareToken ? 'Go home' : publicView ? 'Back to public projects' : 'Back to My Projects'
          }</button>
        </div>
      </div>
    `;
    app.querySelector('[data-back]')!.addEventListener('click', () =>
      shareToken ? navigate('/') : publicView ? navigate('/public') : navigate('/projects')
    );
    return;
  }

  const access: ProjectAccess = shareToken || publicView ? 'share' : (project.access || 'owner');
  const readOnly = access !== 'owner';
  const canComment = access === 'owner' || access === 'member';

  const activeTab = TABS.some((t) => t.key === tab) ? tab : 'overview';

  const base = shareToken
    ? `/share/${shareToken}`
    : publicView
    ? `/public/${id}`
    : `/projects/${id}`;
  const projectHref = (t: string, epicIdParam?: string) =>
    epicIdParam ? `${base}/${t}/${epicIdParam}` : `${base}/${t}`;

  const ctx: ProjectContext = {
    project,
    tasks,
    epics,
    readOnly,
    canComment,
    goTab: (t) => navigate(projectHref(t)),
    goEpics: () => navigate(projectHref('epics')),
    goEpic: (epicIdParam) => navigate(projectHref('epics', epicIdParam)),
    projectHref,
    reload: () => refresh(),
  };

  app.innerHTML = `
    <div class="project-shell">
      ${
        publicView
          ? `
        <div class="share-banner">
          <span class="share-banner-icon">🌐</span>
          <div>
            <strong>Public project</strong>
            <span>This project is public. You can browse it in read-only mode.</span>
          </div>
        </div>
      `
          : shareToken
          ? `
        <div class="share-banner">
          <span class="share-banner-icon">👁</span>
          <div>
            <strong>Read-only view</strong>
            <span>You can browse this project but only the owner can make changes.</span>
          </div>
        </div>
      `
          : access === 'member'
          ? `
        <div class="share-banner">
          <span class="share-banner-icon">💬</span>
          <div>
            <strong>Member access</strong>
            <span>You can view this project and add comments, but only the owner can edit.</span>
          </div>
        </div>
      `
          : ''
      }
      <div class="project-head">
        <div>
          <h1>${escapeHtml(project.name)}</h1>
          <p class="project-desc">${escapeHtml(project.description) || '<span class="muted">No description yet.</span>'}</p>
        </div>
        ${
          readOnly
            ? ''
            : `
          <div style="display:flex;gap:10px;flex-wrap:wrap;">
            <button class="btn btn-ghost" id="share-project">Share</button>
            <button class="btn btn-ghost" id="edit-project">Edit project</button>
          </div>
        `
        }
      </div>
      <div class="tabs">
        ${TABS.map(
          (t) =>
            `<button class="tab ${t.key === activeTab ? 'active' : ''}" data-tab="${t.key}">${t.label}</button>`
        ).join('')}
      </div>
      <div id="tab-content"></div>
    </div>
  `;

  app.querySelectorAll<HTMLElement>('[data-tab]').forEach((btn) => {
    btn.addEventListener('click', () => ctx.goTab(btn.dataset.tab!));
  });

  if (!readOnly) {
    app.querySelector('#share-project')!.addEventListener('click', () => {
      openShareModal(project);
    });
    app.querySelector('#edit-project')!.addEventListener('click', () => {
      openEditProjectModal(project, () => refresh());
    });
  }

  const content = app.querySelector('#tab-content') as HTMLElement;
  if (activeTab === 'overview') await renderOverview(content, ctx);
  else if (activeTab === 'epics') {
    if (subId) await renderEpicDetail(content, ctx, subId);
    else await renderEpics(content, ctx);
  } else if (activeTab === 'tasks') await renderTasks(content, ctx);
  else if (activeTab === 'sprints') await renderSprints(content, ctx, subId);
  else if (activeTab === 'members') await renderMembers(content, ctx);
  else if (activeTab === 'reports') await renderReports(content, ctx);
}
